import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Status, TextField } from '@components';
import { ModalContext } from '@context';
import { EmailVerificationService } from '@data';
import { VerifyEmailCheckPayload } from '@types';

import styles from './verifyEmailAddress.module.scss';

/**
 * The state of the component step
 */
enum PageState {
	NEW,
	VERIFY_SENT,
	VERIFY_SUCCESS,
	VERIFY_CHECK,
	VERIFY_FAILED,
}

/**
 * The state of the Email Verification
 */
enum EmailVerificationState {
	VERIFICATION_FAILED = 'Email verification failed. Please try again later.',
	REQUIRES_VERIFICATION = 'A verification code has been sent. Please check your email and enter the code into this screen to continue the verification process.',
	VERIFIED = 'Your email address has been verified.',
	VERIFIED_CHECK_FAILED = "Incorrect verification code. Please check the code has been entered correctly and has not expired. Select 're-send code' to receive another verification code via email.",
}

export const VerifyEmailAddress = () => {
	// Context
	const { emailAddress, setShowEmailAddressModal, setEmailAddressFromModal } =
		useContext(ModalContext);

	// State
	const [pageState, setPageState] = useState<PageState>(PageState.NEW);
	const [verificationCode, setVerificationCode] = useState<string>('');
	const [message, setMessage] = useState<string>('');
	const [verifyEmailAddress, setVerifyEmailAddress] = useState<string>(
		emailAddress ? emailAddress : '',
	);

	// Cooldown timer
	const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
	const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const startCooldown = () => {
		const COOLDOWN = 30;
		setCooldownSeconds(COOLDOWN);
		if (cooldownRef.current) clearInterval(cooldownRef.current);
		cooldownRef.current = setInterval(() => {
			setCooldownSeconds(prev => {
				if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
				return prev - 1;
			});
		}, 1000);
	};

	useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

	// Data
	const verifyCheckEmail = useMutation({
		mutationFn: (payload: VerifyEmailCheckPayload) =>
			EmailVerificationService.VerifyCheckEmail(payload.emailAddress, payload.code),
	});

	const sendVerification = useMutation({
		mutationFn: (emailAddress: string) => EmailVerificationService.VerifyEmail(emailAddress),
	});

	const verify = async () => {
		if (verifyEmailAddress && !sendVerification.isPending) {
			try {
				const response = await sendVerification.mutateAsync(verifyEmailAddress);
				switch (response.responseCode) {
					case 'VERIFIED':
					// In case this is the response code, display same scenario than 'REQUESTED'
					case 'REQUESTED':
						startCooldown();
						setMessage(EmailVerificationState.REQUIRES_VERIFICATION);
						setPageState(PageState.VERIFY_SENT);
						break;
					default:
						setMessage(EmailVerificationState.VERIFICATION_FAILED);
						setPageState(PageState.VERIFY_FAILED);
						break;
				}
			} catch {
				setMessage(EmailVerificationState.VERIFICATION_FAILED);
				setPageState(PageState.VERIFY_FAILED);
			}
		}
	};

	const checkCode = async () => {
		if (verificationCode.length != 0) {
			await verifyCheckEmail
				.mutateAsync({
					emailAddress: verifyEmailAddress,
					code: verificationCode,
				})
				.then(response => {
					switch (response.responseCode) {
						case 'VERIFIED':
							setMessage(EmailVerificationState.VERIFIED);
							setPageState(PageState.VERIFY_SUCCESS);
							break;
						case 'FAILURE':
							setMessage(EmailVerificationState.VERIFIED_CHECK_FAILED);
							setVerificationCode('');
							setPageState(PageState.VERIFY_CHECK);
							break;
						default:
							setMessage(EmailVerificationState.VERIFIED_CHECK_FAILED);
							setVerificationCode('');
							setPageState(PageState.VERIFY_FAILED);
					}
				})
				.catch(() => {
					setMessage(EmailVerificationState.VERIFIED_CHECK_FAILED);
					setVerificationCode('');
					setPageState(PageState.VERIFY_CHECK);
				});
		}
	};

	const cancel = () => {
		setShowEmailAddressModal(false);
	};

	const save = () => {
		setShowEmailAddressModal(false);
		setEmailAddressFromModal(verifyEmailAddress);
	};

	const StatusComponent = () => {
		return pageState == PageState.VERIFY_SUCCESS ||
			pageState == PageState.VERIFY_SENT ||
			pageState == PageState.VERIFY_FAILED ||
			pageState == PageState.VERIFY_CHECK ? (
			<div className={styles.status}>
				<Status
					status={
						pageState == PageState.VERIFY_SUCCESS || pageState == PageState.VERIFY_SENT
							? 'success'
							: 'error'
					}
					show={message.length > 0}
					title=" "
					msg={message}
					onClose={() => setMessage('')}
					closable={false}
				/>
			</div>
		) : (
			<div className={styles.status}></div>
		);
	};

	return (
		<div className={styles.content}>
			<h3 className={styles.header}>Update email address</h3>
			<div className={styles.instruction}>
				Enter the new email address and click on &apos;Verify email&apos; button
			</div>
			<div className={styles.label}>Email address</div>
			<TextField
				placeholder="Enter email address"
				disabled={pageState != PageState.NEW && pageState != PageState.VERIFY_FAILED}
				defaultValue={verifyEmailAddress}
				handleChange={e => setVerifyEmailAddress(e.target.value)}
				handleSubmit={verify}
			/>
			<Button
				customStyles={styles.button}
				onClick={verify}
				isLoading={sendVerification.isPending}
				size="small"
				type="primary"
				autoFocus={true}
				aria-label="Verify"
				data-testid="verify"
				disabled={sendVerification.isPending || cooldownSeconds > 0 || (pageState != PageState.NEW && pageState != PageState.VERIFY_FAILED)}
			>
				Verify email
			</Button>
			<div />
			{pageState == PageState.VERIFY_CHECK || pageState == PageState.VERIFY_SENT ? (
				<div className={styles.label}>Please enter verification code received in email</div>
			) : (
				<div />
			)}
			{pageState == PageState.VERIFY_CHECK || pageState == PageState.VERIFY_SENT ? (
				<TextField
					placeholder="Enter verification code"
					defaultValue={verificationCode}
					handleSubmit={checkCode}
					handleChange={e => {
						setVerificationCode(e.target.value);
						setMessage(''); // hide message
					}}
				/>
			) : (
				<div />
			)}
			{pageState == PageState.VERIFY_CHECK || pageState == PageState.VERIFY_SENT ? (
				<Button
					customStyles={styles.button}
					onClick={checkCode}
					isLoading={verifyCheckEmail.isPending}
					size="small"
					type="primary"
					autoFocus={true}
					aria-label="Verify"
					data-testid="verify"
					disabled={verificationCode.length == 0}
				>
					Verify code
				</Button>
			) : (
				<div />
			)}
			{pageState == PageState.VERIFY_CHECK || pageState == PageState.VERIFY_SENT ? (
				<Button
					customStyles={styles.button}
					onClick={verify}
					isLoading={sendVerification.isPending}
					size="small"
					type="primary"
					autoFocus={true}
					aria-label="resend"
					data-testid="resend"
					disabled={sendVerification.isPending || cooldownSeconds > 0}
				>
					{cooldownSeconds > 0 ? `Re-send code (${cooldownSeconds}s)` : 'Re-send code'}
				</Button>
			) : (
				<div />
			)}
			{StatusComponent()}

			<div />
			<div />
			<Button customStyles={styles.button} onClick={cancel} size="small" type="secondary">
				Cancel
			</Button>
			<Button
				customStyles={[styles.button, styles['bottom-animation-gap']].join(' ')}
				onClick={save}
				size="small"
				type="primary"
				disabled={pageState != PageState.VERIFY_SUCCESS}
			>
				OK
			</Button>
		</div>
	);
};
